/**
* 左边菜单 — 深色侧边栏（AppLayout.tsx 风格）
* 背景 #16202d，边框 #1e2d3e
* 分组标题 10px 全大写 #4d6b82，可折叠
* 菜单项 12.5px #8ab4cc，激活态白色 + 左侧蓝条 #0073bb
*/
<template>
  <div class="dark-sidebar">
    <!-- Logo 区域 -->
    <div class="sidebar-logo">
      <div class="logo-mark">
        <span>V</span>
      </div>
      <div class="logo-text" v-if="!collapsed">
        <div class="logo-title">VSTECS</div>
        <div class="logo-subtitle">bill-pro</div>
      </div>
    </div>

    <!-- 导航区域 -->
    <nav class="sidebar-nav">
      <div v-for="menu in allmenu" :key="menu.id" class="nav-group">
        <!-- 有子菜单 = 分组 -->
        <template v-if="menu.hasChildren">
          <!-- 分组标题（可折叠） -->
          <button
            class="group-header"
            @click="toggleGroup(menu.id)"
          >
            <span class="group-label">{{ menu.text }}</span>
            <el-icon class="group-chevron" :class="{ 'is-collapsed': collapsedGroups[menu.id] }">
              <ArrowDown />
            </el-icon>
          </button>

          <!-- 分组内菜单项 -->
          <div v-show="!collapsedGroups[menu.id]" class="group-items">
            <template v-for="chmenu in menu.children" :key="chmenu.id">
              <!-- 二级子菜单（嵌套分组） -->
              <template v-if="chmenu.hasChildren">
                <button
                  class="group-header sub-group"
                  @click="toggleGroup(chmenu.id)"
                >
                  <span class="group-label">{{ chmenu.text }}</span>
                  <el-icon class="group-chevron" :class="{ 'is-collapsed': collapsedGroups[chmenu.id] }">
                    <ArrowDown />
                  </el-icon>
                </button>
                <div v-show="!collapsedGroups[chmenu.id]" class="group-items">
                  <router-link
                    v-for="cchmenu in chmenu.children"
                    :key="cchmenu.id"
                    :to="'/' + cchmenu.attributes.url"
                    custom
                    v-slot="{ navigate, isActive }"
                  >
                    <button
                      class="nav-item"
                      :class="{ 'is-active': isActive || isRouteActive(cchmenu) }"
                      @click="onNavClick(cchmenu, navigate)"
                    >
                      <span class="active-bar"></span>
                      <el-icon class="nav-icon">
                        <component :is="cchmenu.attributes.icon || 'Menu'" />
                      </el-icon>
                      <span class="nav-label">{{ cchmenu.text }}</span>
                    </button>
                  </router-link>
                </div>
              </template>

              <!-- 普通菜单项 -->
              <template v-else>
                <router-link
                  :to="'/' + chmenu.attributes.url"
                  custom
                  v-slot="{ navigate, isActive }"
                >
                  <button
                    class="nav-item"
                    :class="{ 'is-active': isActive || isRouteActive(chmenu) }"
                    @click="onNavClick(chmenu, navigate)"
                  >
                    <span class="active-bar"></span>
                    <el-icon class="nav-icon">
                      <component :is="chmenu.attributes.icon || 'Menu'" />
                    </el-icon>
                    <span class="nav-label">{{ chmenu.text }}</span>
                    <!-- 费用告警 badge -->
                    <span v-if="chmenu.attributes.url === 'costAlerts' && alertCount > 0" class="nav-badge">
                      {{ alertCount > 99 ? '99+' : alertCount }}
                    </span>
                  </button>
                </router-link>
              </template>
            </template>
          </div>
        </template>

        <!-- 无子菜单 = 顶级菜单项 -->
        <template v-else>
          <router-link
            :to="'/' + menu.attributes.url"
            custom
            v-slot="{ navigate, isActive }"
          >
            <button
              class="nav-item top-level"
              :class="{ 'is-active': isActive || isRouteActive(menu) }"
              @click="onNavClick(menu, navigate)"
            >
              <span class="active-bar"></span>
              <el-icon class="nav-icon">
                <component :is="menu.attributes.icon || 'Menu'" />
              </el-icon>
              <span class="nav-label">{{ menu.text }}</span>
            </button>
          </router-link>
        </template>
      </div>
    </nav>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useMutitabsStore } from '@/store/mutitabs'
import { getStorage } from '@/utils/util'
import { ArrowDown } from '@element-plus/icons-vue'

let bus = getCurrentInstance().appContext.config.globalProperties.$Bus
const mutitabsStore = useMutitabsStore()
const router = useRouter()
const route = useRoute()

let collapsed = ref(false)
let allmenu = ref([])
let collapsedGroups = ref({})
let alertCount = ref(0)

function toggleGroup(id) {
  collapsedGroups.value[id] = !collapsedGroups.value[id]
}

function isRouteActive(menu) {
  if (!menu.attributes || !menu.attributes.url) return false
  return route.path === '/' + menu.attributes.url || route.name === menu.attributes.url
}

function onNavClick(menu, navigate) {
  mutitabsStore.editableTabs(menu)
  navigate()
}

function getMenu() {
  allmenu.value = JSON.parse(getStorage('allmenu')) || []
  // 默认折叠 SYSTEM / MONITORING 分组
  allmenu.value.forEach(menu => {
    if (menu.hasChildren) {
      const label = (menu.text || '').toUpperCase()
      if (label === 'SYSTEM' || label === 'MONITORING') {
        if (collapsedGroups.value[menu.id] === undefined) {
          collapsedGroups.value[menu.id] = true
        }
      }
    }
  })
}

// 加载告警数量
function loadAlertCount() {
  try {
    const warningList = localStorage.getItem('warningList')
    if (warningList) {
      alertCount.value = JSON.parse(warningList).length
    }
  } catch (e) { /* ignore */ }
}

onMounted(() => {
  getMenu()
  loadAlertCount()
  bus.on('toggle', value => {
    collapsed.value = !value
  })
  bus.on('routeReload', () => {
    getMenu()
  })
})

onBeforeRouteUpdate(to => {
  mutitabsStore.switchtabNoRoute(to.name)
})
</script>

<style lang="scss" scoped>
/* ── 深色侧边栏容器 ── */
.dark-sidebar {
  height: 100%;
  background: #16202d;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "PingFang SC", "Helvetica Neue", "Microsoft YaHei", sans-serif;
}

/* ── Logo 区域 ── */
.sidebar-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 16px;
  border-bottom: 1px solid #1e2d3e;
  flex-shrink: 0;
  min-height: 56px;
  box-sizing: border-box;
}

.logo-mark {
  width: 36px;
  height: 36px;
  border-radius: 7px;
  background: #0073bb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  span {
    color: #fff;
    font-weight: 700;
    font-size: 16px;
    letter-spacing: -0.02em;
  }
}

.logo-text {
  min-width: 0;
  flex: 1;
}

.logo-title {
  font-weight: 700;
  color: #fff;
  font-size: 17px;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.logo-subtitle {
  font-size: 13px;
  color: #7a9bb5;
  line-height: 1.2;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: 0.02em;
  font-weight: 400;
}

/* ── 导航滚动区 ── */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #2a3a4e; border-radius: 2px; }
  &::-webkit-scrollbar-thumb:hover { background: #3a4e66; }
}

/* ── 分组 ── */
.nav-group {
  margin-bottom: 4px;
}

/* ── 分组标题 ── */
.group-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color 0.15s;

  &:hover .group-label { color: #6b8fa8; }
  &:hover .group-chevron { color: #6b8fa8; }

  &.sub-group {
    padding: 8px 16px 4px 24px;
  }
}

.group-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #4d6b82;
  transition: color 0.15s;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", sans-serif;
}

.group-chevron {
  font-size: 10px;
  color: #4d6b82;
  transition: transform 0.2s, color 0.15s;

  &.is-collapsed {
    transform: rotate(-90deg);
  }
}

/* ── 菜单项 ── */
.group-items {
  padding: 0;
}

.nav-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 12px 0 16px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #8ab4cc;
  font-size: 12.5px;
  letter-spacing: -0.005em;
  transition: all 0.15s;
  text-align: left;
  border-radius: 0 3px 3px 0;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.06);
  }

  &.top-level {
    margin-top: 2px;
  }

  /* ── 激活态 ── */
  &.is-active {
    color: #fff;
    background: rgba(0, 115, 187, 0.2);

    .active-bar {
      opacity: 1;
    }

    .nav-icon {
      color: #4db8ff !important;
    }
  }
}

/* 左侧蓝色竖条 */
.active-bar {
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 2.5px;
  border-radius: 0 9999px 9999px 0;
  background: #0073bb;
  opacity: 0;
  transition: opacity 0.15s;
}

/* 图标 */
.nav-icon {
  font-size: 15px;
  color: inherit;
  flex-shrink: 0;
  transition: color 0.15s;
}

/* 文字 */
.nav-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12.5px;
  font-weight: 400;
  color: inherit;
}

/* Badge（费用告警） */
.nav-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  background: rgba(239, 68, 68, 0.9);
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}
</style>
